import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import CreatableSelect from 'react-select/creatable';
import './routeDialog.css';

/**
 * Add / remove a driver route. Spec: PLAN_driver_routing.md
 *
 * Replaces window.prompt and window.confirm, which rendered as raw Chrome
 * dialogs in the middle of an otherwise designed page — and, worse, gave no
 * room to say what removing a route actually does to its bookings.
 *
 * Rendered through a portal so the page's own stacking and overflow can never
 * clip it.
 */
export default function RouteDialog({
    mode, route, bookingCount = 0, suggestion = '', areaOptions = [], busy, onSubmit, onClose,
}) {
    // { value, label, isArea } — isArea marks a real Dubai area, which lets the
    // new route take that area's bookings instead of starting empty.
    const [choice, setChoice] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (mode === 'add') {
            setChoice(null);
            // Focus after paint so the caret is ready without the user clicking.
            const t = setTimeout(() => inputRef.current?.focus(), 40);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [mode]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && !busy) onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [busy, onClose]);

    if (!mode) return null;

    const isAdd = mode === 'add';
    const canSubmit = isAdd ? !!choice?.value?.trim() && !busy : !busy;

    const submit = (e) => {
        e?.preventDefault();
        if (!canSubmit) return;
        onSubmit(isAdd
            ? { name: choice.value.trim(), claimArea: choice.isArea ? choice.value : null }
            : null);
    };

    return ReactDOM.createPortal(
        <div className="routeDlg__scrim" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
            <div className="routeDlg" role="dialog" aria-modal="true">
                <h3 className="routeDlg__title">
                    {isAdd ? 'Add a route' : `Remove ${route?.route_name}?`}
                </h3>

                {isAdd ? (
                    <form onSubmit={submit}>
                        <p className="routeDlg__text">
                            A route is an area one driver covers for the day — name it after the
                            patch of Dubai it serves, not the driver, since drivers change and
                            the area does not. Downtown and Marina are always here.
                        </p>
                        <label className="routeDlg__label" htmlFor="routeDlgName">Route name</label>
                        <CreatableSelect
                            inputId="routeDlgName"
                            ref={inputRef}
                            classNamePrefix="routeDlgSelect"
                            autoFocus
                            isClearable
                            options={areaOptions}
                            value={choice}
                            onChange={(opt) => setChoice(opt)}
                            onCreateOption={(v) => setChoice({ value: v, label: v, isArea: false })}
                            formatCreateLabel={(v) => `Use "${v}" as the route name`}
                            placeholder={suggestion ? `For example ${suggestion}..` : 'For example JVC..'}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (b) => ({ ...b, zIndex: 4200 }) }}
                        />
                        {choice?.isArea && (
                            <p className="routeDlg__hint">
                                Today&apos;s <strong>{choice.label}</strong> bookings move onto this route,
                                and new ones will follow it from now on.
                            </p>
                        )}

                        <div className="routeDlg__actions">
                            <button type="button" className="routeDlg__btn" onClick={onClose} disabled={busy}>
                                Cancel
                            </button>
                            <button type="submit" className="routeDlg__btn routeDlg__btn--primary" disabled={!canSubmit}>
                                {busy ? 'Adding…' : 'Add route'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <>
                        <p className="routeDlg__text">
                            {bookingCount > 0
                                ? <>Its <strong>{bookingCount} booking{bookingCount === 1 ? '' : 's'}</strong> move back
                                    to the route their area belongs to — Downtown or Marina. Nothing is
                                    left without a driver.</>
                                : <>This route has no bookings on it.</>}
                        </p>
                        <div className="routeDlg__actions">
                            <button type="button" className="routeDlg__btn" onClick={onClose} disabled={busy}>
                                Keep it
                            </button>
                            <button
                                type="button"
                                className="routeDlg__btn routeDlg__btn--danger"
                                onClick={submit}
                                disabled={busy}
                            >
                                {busy ? 'Removing…' : 'Remove route'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
}
